import { Injectable, inject } from '@angular/core';
import { lastValueFrom, Observable } from 'rxjs';
import { KycService } from './kyc.service';
import { ConfigService } from '@app/core/config/config.service';
import { KycStateDocument } from '../interfaces/kyc';

export type KycStep = string; // Backend-driven

/** Mapeo step → ruta para el flujo KYC. Los nombres exactos los define el
 *  backend en steps[]. Actualizado 2026-05-21 para incluir IDENTITY_VERIFICATION
 *  y FORM_INFO — antes faltaban y `processNextTransition` saltaba directo de
 *  FINANCIAL_PROFILE a DOCUMENTS y de DOCUMENTS a REVIEW, dejando dos pantallas
 *  del sidebar (Verificación de identidad y Formulario) fuera del state machine.
 *
 *  Mapping sidebar ↔ step backend ↔ ruta:
 *   • Verificación de identidad → IDENTITY_VERIFICATION → /cards/kyc/documentation
 *     (componente de drag-zones con INE_CARD + SELFIE).
 *   • Documentación              → DOCUMENTS            → /cards/kyc/representative-data
 *     (componente con rep legal + cliente + propietario + proveedor + benefs).
 *   • Formulario                 → FORM_INFO            → /cards/kyc/applicant-form
 *     (solicitant-form subset CARDS).
 */
const KYC_ROUTES: Record<string, string> = {
  CONTACT_INFO: '/cards/kyc',
  LABOR_INFO: '/cards/kyc/company-data',
  COMPANY_INFO: '/cards/kyc/company-data',          // alias defensivo
  FINANCIAL_PROFILE: '/cards/kyc/financial-profile',
  IDENTITY_VERIFICATION: '/cards/kyc/documentation',
  DOCUMENTS: '/cards/kyc/representative-data',
  FORM_INFO: '/cards/kyc/applicant-form',
  REVIEW: '/cards/kyc/final-registration',
  FINISHED: '/cards/kyc/request-sent',
};

export interface KycProcessMeta {
  id: string;
  folio?: number;
  personType: string;
  product: string;
  currentStep: KycStep;
  status: string;
  phoneVerified: boolean;
  verifiedPhone: string | null;
  privacyPolicyAccepted: boolean;
}

/** Guarda el estado del flujo KYC + lógica de navegación backend-driven.
 *  Paralelo al KybFlowStateService pero con las steps y rutas del flujo KYC. */
@Injectable({
  providedIn: 'root',
})
export class KycFlowStateService {
  private readonly kycApi = inject(KycService);
  private readonly configService = inject(ConfigService);

  /** PersonType local (PF, PFAE, PM) — fallback antes de que el primer hydrate
   *  llene processMeta. Persistido en sessionStorage para sobrevivir F5.
   *  ProductsComponent lo setea antes de POST /start; el resto del flow lo lee
   *  vía `personType` getter (que prefiere processMeta cuando ya hidrató). */
  private localPersonType: 'PF' | 'PFAE' | 'PM' = 'PF';

  constructor() {
    const saved = sessionStorage.getItem('kycPersonType');
    if (saved === 'PFAE' || saved === 'PM' || saved === 'PF') {
      this.localPersonType = saved;
    }
  }

  private contactInfo: Record<string, unknown> | null = null;
  private laborInfo: Record<string, unknown> | null = null;
  private financialProfile: Record<string, unknown> | null = null;
  private companyInfo: Record<string, unknown> | null = null;
  private satInfo: Record<string, unknown> | null = null;
  private documentation: Record<string, unknown> | null = null;

  private _processMeta: KycProcessMeta | null = null;
  private _stepsArray: KycStep[] = [];
  private _shownStep: KycStep | null = null;
  private _hydrated = false;
  private _hydrating: Promise<void> | null = null;
  private _stateDocuments: KycStateDocument[] = [];

  // ─── Form state ─────────────────────────────────────────────────
  getContactInfo() { return this.contactInfo; }
  setContactInfo(v: Record<string, unknown> | null) { this.contactInfo = v; }

  getLaborInfo() { return this.laborInfo; }
  setLaborInfo(v: Record<string, unknown> | null) { this.laborInfo = v; }

  getFinancialProfile() { return this.financialProfile; }
  setFinancialProfile(v: Record<string, unknown> | null) { this.financialProfile = v; }

  getCompanyInfo() { return this.companyInfo; }
  setCompanyInfo(v: Record<string, unknown> | null) { this.companyInfo = v; }

  getSatInfo() { return this.satInfo; }
  setSatInfo(v: Record<string, unknown> | null) { this.satInfo = v; }

  getDocumentation() { return this.documentation; }
  setDocumentation(v: Record<string, unknown> | null) { this.documentation = v; }

  // ─── Backend-driven state + nav ─────────────────────────────────
  get isHydrated(): boolean { return this._hydrated; }
  get processMeta(): KycProcessMeta | null { return this._processMeta; }
  get stepsArray(): KycStep[] { return this._stepsArray; }
  get currentStep(): KycStep | null { return this._processMeta?.currentStep ?? null; }
  get shownStep(): KycStep | null { return this._shownStep; }
  get stateDocuments(): KycStateDocument[] { return this._stateDocuments; }
  get phoneVerified(): boolean { return this._processMeta?.phoneVerified === true; }
  get verifiedPhone(): string | null { return this._processMeta?.verifiedPhone ?? null; }

  /** PersonType activo. Prioridad: processMeta del backend > fallback local. */
  get personType(): 'PF' | 'PFAE' | 'PM' {
    const fromMeta = this._processMeta?.personType;
    if (fromMeta === 'PF' || fromMeta === 'PFAE' || fromMeta === 'PM') return fromMeta;
    return this.localPersonType;
  }

  /** True cuando el flujo CARDS corre para persona moral. Los componentes lo
   *  consumen para renderizar variantes PM (ej. sección "Empresa" en lugar
   *  de "Rep legal" + "Cliente" en la pantalla de Documentación). */
  get isPM(): boolean { return this.personType === 'PM'; }

  /** Lo invoca ProductsComponent antes de POST /start para alinear el
   *  fallback local con el tipo de persona elegido (PFAE o PM en CARDS).
   *  En mock alimenta a seedMockState para que processMeta.personType sea
   *  consistente con la selección del usuario. */
  setPersonType(value: 'PF' | 'PFAE' | 'PM'): void {
    this.localPersonType = value;
    sessionStorage.setItem('kycPersonType', value);
  }

  /** Persiste un doc recién subido en stateDocuments para que sobreviva a la
   *  navegación entre steps (sin esto, sólo los docs del hydrate inicial vuelven
   *  a renderizar al revisitar Documentos). Reemplaza si ya existe el documentType. */
  addStateDocument(doc: { documentId: number; documentType: string; s3Key: string; status: string }): void {
    const idx = this._stateDocuments.findIndex(d => d.documentType === doc.documentType);
    if (idx >= 0) {
      this._stateDocuments[idx] = doc;
    } else {
      this._stateDocuments.push(doc);
    }
  }

  /** Remueve un doc del state local por documentType. No toca backend — se
   *  invoca desde el handler de "Eliminar" para que applyStateDocuments no
   *  rehidrate el slot al revisitar. */
  removeStateDocumentByType(documentType: string): void {
    this._stateDocuments = this._stateDocuments.filter(d => d.documentType !== documentType);
  }

  /** Rehidrata desde GET /state. Idempotente; concurrent-safe. */
  async hydrateFromBackend(force = false): Promise<void> {
    if (this._hydrated && !force) return;
    // Modo mock (revisión de maquetas): no llamamos /state; sembramos un processMeta
    // y stepsArray ficticios para que getNextStep/advanceCurrentStep funcionen.
    if (this.configService.isMockBackend) {
      this.seedMockState();
      return;
    }
    if (this._hydrating) return this._hydrating;
    this._hydrating = this.doHydrate();
    try { await this._hydrating; } finally { this._hydrating = null; }
  }

  private seedMockState(): void {
    this._processMeta = {
      id: 'MOCK_ID',
      // En mock seedeamos con el personType local (set por ProductsComponent
      // antes del navigate) para que `isPM` y demás getters respeten PFAE/PM.
      personType: this.localPersonType,
      product: 'CARDS',
      currentStep: 'CONTACT_INFO',
      status: 'IN_PROGRESS',
      phoneVerified: true,
      verifiedPhone: null,
      privacyPolicyAccepted: true,
    };
    // 2026-05-21: agregamos IDENTITY_VERIFICATION (paso 5 sidebar) y FORM_INFO
    // (paso 7 sidebar). Sin estos, `getNextStep()` saltaba pantallas y el
    // representative-data terminaba mandando al usuario de regreso a docs.
    this._stepsArray = [
      'CONTACT_INFO',
      'LABOR_INFO',
      'FINANCIAL_PROFILE',
      'IDENTITY_VERIFICATION',
      'DOCUMENTS',
      'FORM_INFO',
      'REVIEW',
      'FINISHED',
    ];
    this._stateDocuments = [];
    if (!this._shownStep) this._shownStep = 'CONTACT_INFO';
    this._hydrated = true;
  }

  private async doHydrate(): Promise<void> {
    const res: any = await lastValueFrom(this.kycApi.getState());
    if (!res?.success || !res?.data) throw new Error('KYC hydrate failed');
    const s = res.data;
    this._processMeta = s.process;
    this._stepsArray = s.steps || [];
    this._stateDocuments = s.documents || [];
    if (!this._shownStep) this._shownStep = s.process?.currentStep ?? null;

    if (s.contactInfo) this.contactInfo = s.contactInfo;
    if (s.laborInfo) this.laborInfo = s.laborInfo;
    if (s.financialProfile) this.financialProfile = s.financialProfile;
    if (s.companyInfo) this.companyInfo = s.companyInfo;
    if (s.satInfo) this.satInfo = s.satInfo;
    this._hydrated = true;
  }

  setShownStep(step: KycStep): void {
    this._shownStep = step;
  }

  /** "Siguiente" llama /next-step solo si shownStep === currentStep (avance real). */
  shouldCallNextStep(): boolean {
    if (!this._shownStep || !this._processMeta) return true;
    return this._shownStep === this._processMeta.currentStep;
  }

  /** Actualiza el step maximo alcanzado en backend. NO toca shownStep: la pagina destino
   * reseteara shownStep en su ngOnInit. Si aqui moviesemos shownStep, getNextStep() saltaria
   * una pantalla. */
  advanceCurrentStep(newStep: KycStep): void {
    if (this._processMeta) this._processMeta.currentStep = newStep;
  }

  getNextStep(): KycStep | null {
    if (!this._shownStep || !this._stepsArray.length) return null;
    const idx = this._stepsArray.indexOf(this._shownStep);
    if (idx === -1 || idx >= this._stepsArray.length - 1) return null;
    return this._stepsArray[idx + 1];
  }

  getPreviousStep(): KycStep | null {
    if (!this._shownStep || !this._stepsArray.length) return null;
    const idx = this._stepsArray.indexOf(this._shownStep);
    if (idx <= 0) return null;
    return this._stepsArray[idx - 1];
  }

  getRouteForStep(step: KycStep | null): string | null {
    if (!step) return null;
    return KYC_ROUTES[step] ?? null;
  }

  /** Helper para "Siguiente": ejecuta save$, decide si llama /next-step, retorna la ruta. */
  async processNextTransition(save$: Observable<any>): Promise<string | null> {
    // Modo mock (revisión de maquetas): avanza localmente sin tocar backend.
    if (this.configService.isMockBackend) {
      const next = this.getNextStep();
      if (next) this.advanceCurrentStep(next);
      return this.getRouteForStep(next);
    }
    await lastValueFrom(save$);
    if (this.shouldCallNextStep()) {
      const res: any = await lastValueFrom(this.kycApi.nextStep());
      const newStep = res?.data as KycStep | undefined;
      if (newStep) this.advanceCurrentStep(newStep);
    }
    return this.getRouteForStep(this.getNextStep());
  }

  /** Helper para "Regresar": retorna la ruta del step anterior. */
  processBackTransition(): string | null {
    const prev = this.getPreviousStep();
    if (!prev) return null;
    this.setShownStep(prev);
    return this.getRouteForStep(prev);
  }

  markPhoneUnverified(): void {
    if (this._processMeta) {
      this._processMeta.phoneVerified = false;
      this._processMeta.verifiedPhone = null;
    }
  }

  clearAll(): void {
    this.contactInfo = null;
    this.laborInfo = null;
    this.financialProfile = null;
    this.companyInfo = null;
    this.satInfo = null;
    this.documentation = null;
    this._processMeta = null;
    this._stepsArray = [];
    this._shownStep = null;
    this._hydrated = false;
    this._stateDocuments = [];
  }
}
